import React, { useCallback, useEffect, useState } from 'react';
import { photoAuth, photoUrl, removePhoto } from '../lib/photos';
import { initials, hueOf } from '../lib/initials';
import './Avatar.css';

/**
 * EVERY PROFILE PHOTO, FOR AN ADMINISTRATOR TO TAKE ONE DOWN (Settings).
 *
 * An administrator may remove anybody's photo - the firm needs a way to take
 * down one that should not be there - but cannot choose one for anybody: a
 * face is somebody's own to pick. So this lists them all, the ones the
 * setting is hiding included (they are still stored), each with Remove and
 * an inline "are you sure", and nothing to upload.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/** One small picture, fetched as an administrator moderating. */
function Thumb({ person }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let live = true;
    let made = null;
    fetch(`${photoUrl(person.key, person.version, 96)}&moderate=1`, { headers: photoAuth() })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b && live) { made = URL.createObjectURL(b); setSrc(made); } })
      .catch(() => {});
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [person.key, person.version]);
  return (
    <span className="ar-photo-face jv-av" data-hue={hueOf(person.key)} data-photo={src ? 'on' : undefined} aria-hidden="true">
      <span className="jv-av-text">{initials(person.name)}</span>
      {src && <img className="jv-av-img" data-loaded="" src={src} alt="" />}
    </span>
  );
}

export default function PhotoModeration({ canEdit = true }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [asking, setAsking] = useState(null);
  const [busy, setBusy] = useState(null);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/photos/everyone`, { headers: photoAuth() });
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        setList([]);
        setError(r.status === 503 ? (body?.error || 'Run migrate:080 to use profile photos.') : null);
        return;
      }
      const rows = await r.json();
      setList(Array.isArray(rows) ? rows : []);
      setError(null);
    } catch {
      setList([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (p) => {
    setBusy(p.key);
    setError(null);
    try {
      await removePhoto(p.key);
      setDone(`${p.name ? `${p.name}’s` : 'That'} photo is removed and deleted.`);
      setAsking(null);
      await load();
    } catch (e) {
      setError(e.message || 'Could not remove that photo.');
    } finally {
      setBusy(null);
    }
  };

  if (list === null) return <p className="ar-consequence">Loading photos…</p>;
  return (
    <div className="ar-photos">
      {error && <p className="ar-error" role="alert">{error}</p>}
      <p className="ar-status" role="status" aria-live="polite">{done || ''}</p>
      {list.length === 0 && !error ? (
        <p className="ar-consequence">Nobody has added a photo yet.</p>
      ) : (
        <ul className="ar-photo-list">
          {list.map((p) => (
            <li key={p.key} className="ar-photo">
              <Thumb person={p} />
              <span className="ar-photo-name">
                {p.name || 'Somebody'}
                {p.is_contractor && <span className="ar-photo-tag">IT</span>}
              </span>
              {canEdit && asking !== p.key && (
                <button type="button" className="btn btn-secondary btn-small"
                        onClick={() => { setAsking(p.key); setDone(null); }}>
                  Remove
                </button>
              )}
              {canEdit && asking === p.key && (
                <span className="ar-photo-confirm">
                  <button type="button" className="btn btn-danger btn-small" disabled={busy === p.key}
                          onClick={() => remove(p)} autoFocus>
                    {busy === p.key ? 'Removing…' : 'Delete photo'}
                  </button>
                  <button type="button" className="btn btn-secondary btn-small" disabled={busy === p.key}
                          onClick={() => setAsking(null)}>
                    Keep
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
